import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, Plus, ThumbsUp, ThumbsDown, MessageSquare, Search, Wand2, PenLine, Sparkles, GripVertical, ArrowUpDown, Clock } from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';

const STATUS_LABELS = {
  draft:     { label: '초안',   color: 'bg-gray-800 text-gray-400' },
  shared:    { label: '공유됨', color: 'bg-blue-900/40 text-blue-400' },
  reviewing: { label: '검토중', color: 'bg-yellow-900/40 text-yellow-400' },
  adopted:   { label: '채택',   color: 'bg-green-900/40 text-green-400' },
  pending:   { label: '보류',   color: 'bg-red-900/40 text-red-400' },
};

export default function IdeaArchivePage() {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [filter, setFilter] = useState({ type: '', status: 'shared', source: '' });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('priority'); // 'priority' | 'date'
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const me = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = me.role === 'superadmin';

  const load = useCallback(async () => {
    const params = { sort };
    if (filter.type) params.type = filter.type;
    if (filter.status) params.status = filter.status;
    if (filter.source) params.source = filter.source;
    const r = await api.get('/ideas', { params });
    setIdeas(r.data);
  }, [filter, sort]);

  useEffect(() => { load(); }, [load]);

  const filtered = ideas.filter(i =>
    !search || i.title?.includes(search) || i.tags?.includes(search) || i.keywords?.includes(search)
  );

  const vote = async (e, ideaId, type) => {
    e.stopPropagation();
    const r = await api.post(`/ideas/${ideaId}/vote`, { type });
    setIdeas(prev => prev.map(i => i.id === ideaId ? { ...i, votes: r.data } : i));
  };

  const getVotes = (votes = []) => ({
    up: votes.filter(v => v.type === 'up').length,
    down: votes.filter(v => v.type === 'down').length,
    mine: votes.find(v => v.adminId === me.id)?.type,
  });

  // 드래그 정렬
  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDrop = async (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...filtered];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setIdeas(prev => {
      // filtered 기준으로 전체 배열 재구성
      const newAll = [...prev];
      reordered.forEach((item, i) => {
        const pos = newAll.findIndex(a => a.id === item.id);
        if (pos !== -1) newAll[pos] = { ...newAll[pos], sortOrder: i };
      });
      return newAll;
    });
    setDragIdx(null); setDragOverIdx(null);
    await api.put('/ideas/sort-order', {
      orders: reordered.map((item, i) => ({ id: item.id, sortOrder: i })),
    });
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div>
      {/* 헤더 */}
      <PageHeader icon={Lightbulb} title="아이디어 아카이브" count={filtered.length}>
        <button onClick={() => navigate('/idea-write')}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 transition-colors">
          <PenLine size={13} /> 직접 작성
        </button>
        <button onClick={() => navigate('/idea-workshop')}
          className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80">
          <Sparkles size={13} /> AI 워크숍
        </button>
      </PageHeader>

      {/* 소스 탭 + 정렬 */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1">
          {[['', '전체'], ['ai', 'AI 생성'], ['manual', '직접 작성']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(f => ({ ...f, source: v }))}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${filter.source === v ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1">
          <button onClick={() => setSort('priority')}
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors ${sort === 'priority' ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
            <ArrowUpDown size={10} /> 우선순위
          </button>
          <button onClick={() => setSort('date')}
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors ${sort === 'date' ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
            <Clock size={10} /> 등록순
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 flex-1 min-w-[140px]">
          <Search size={13} className="text-gray-600 flex-shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="검색..."
            className="bg-transparent text-white text-sm focus:outline-none flex-1 placeholder-gray-600 min-w-0" />
        </div>
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand">
          <option value="">전체 상태</option>
          <option value="shared">공유됨</option>
          <option value="reviewing">검토중</option>
          <option value="adopted">채택</option>
          <option value="pending">보류</option>
          <option value="draft">초안</option>
        </select>
      </div>

      {isSuperadmin && sort === 'priority' && filtered.length > 1 && (
        <p className="text-gray-600 text-xs mb-3">✦ 드래그하여 우선순위를 변경할 수 있습니다</p>
      )}

      {/* 목록 */}
      {filtered.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl py-16 text-center">
          <Wand2 size={28} className="text-gray-700 mx-auto mb-2" />
          <p className="text-gray-600 text-sm mb-4">아직 아이디어가 없습니다</p>
          <button onClick={() => navigate('/idea-workshop')}
            className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80 mx-auto">
            <Plus size={14} /> 첫 아이디어 만들기
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((idea, idx) => {
            const { up, down, mine } = getVotes(idea.votes);
            const thumb = idea.images?.find(i => i.isSelected) || idea.images?.[0];
            const st = STATUS_LABELS[idea.status] || STATUS_LABELS.shared;
            const isDragging = dragIdx === idx;
            const isDragOver = dragOverIdx === idx && dragIdx !== idx;
            return (
              <div key={idea.id}
                draggable={isSuperadmin && sort === 'priority'}
                onDragStart={isSuperadmin && sort === 'priority' ? e => onDragStart(e, idx) : undefined}
                onDragOver={isSuperadmin && sort === 'priority' ? e => onDragOver(e, idx) : undefined}
                onDrop={isSuperadmin && sort === 'priority' ? e => onDrop(e, idx) : undefined}
                onDragEnd={isSuperadmin && sort === 'priority' ? onDragEnd : undefined}
                style={{ opacity: isDragging ? 0.4 : 1 }}
                className={`bg-gray-900 border rounded-xl overflow-hidden transition-all ${isDragOver ? 'border-brand' : 'border-gray-800 hover:border-gray-700'} ${isSuperadmin && sort === 'priority' ? '' : 'cursor-pointer'}`}
                onClick={() => navigate(`/idea-archive/${idea.id}`)}
              >
                {/* 드래그 핸들 (슈퍼관리자 + 우선순위 모드) */}
                {isSuperadmin && sort === 'priority' && (
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-800/50 cursor-grab active:cursor-grabbing"
                    onClick={e => e.stopPropagation()}>
                    <GripVertical size={14} className="text-gray-700" />
                    <span className="text-gray-700 text-[10px]">우선순위 #{idx + 1}</span>
                  </div>
                )}

                {/* 썸네일 */}
                {thumb ? (
                  <div className="bg-gray-950 overflow-hidden">
                    <img src={thumb.url} alt={idea.title}
                      className="w-full max-h-48 object-contain mx-auto block" />
                  </div>
                ) : idea.type === 'research' && (
                  <div className="h-20 bg-gradient-to-br from-brand/10 to-gray-800 flex items-center justify-center">
                    <Wand2 size={24} className="text-brand/30" />
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-white font-semibold text-sm leading-snug flex-1 truncate">
                      {idea.title || '제목 없음'}
                    </h3>
                    <div className="flex gap-1 flex-shrink-0">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${st.color}`}>{st.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${idea.source === 'manual' ? 'bg-gray-800 text-gray-400' : 'bg-brand/10 text-brand'}`}>
                        {idea.source === 'manual' ? '직접' : 'AI'}
                      </span>
                    </div>
                  </div>
                  {idea.tags && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {idea.tags.split(',').slice(0, 3).map(t => t.trim()).filter(Boolean).map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-500 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-[11px]">
                      {idea.creator?.name || idea.creator?.username} · {new Date(idea.createdAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                    </span>
                    <div className="flex items-center gap-3">
                      <button onClick={e => vote(e, idea.id, 'up')}
                        className={`flex items-center gap-1 text-xs transition-colors ${mine === 'up' ? 'text-green-400' : 'text-gray-600 hover:text-green-400'}`}>
                        <ThumbsUp size={11} /> {up}
                      </button>
                      <button onClick={e => vote(e, idea.id, 'down')}
                        className={`flex items-center gap-1 text-xs transition-colors ${mine === 'down' ? 'text-red-400' : 'text-gray-600 hover:text-red-400'}`}>
                        <ThumbsDown size={11} /> {down}
                      </button>
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        <MessageSquare size={11} /> {idea.comments?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
